/**
 * Scripted lesson facilitator session.
 *
 * Sibling to useVoiceLessonSession. Where that hook infers progression from the
 * participant transcript, this one is tool-driven like useVoiceAssessment: the
 * AI calls record_slide_response / next_slide / complete_session, and the two
 * logging-only tools (log_reframe / log_repeat) leave an auditable row for each
 * command the participant used.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VoiceNavigationIntent } from '../components/lesson-plan/voiceNavigation';
import ApiClient from '../config/ApiClient';
import { LessonPlanSessionModel, SessionSlideModel } from '../object-actions/types/types';
import {
    useVoiceLiveSession,
    VoiceLiveFunctionArgs,
    VoiceLiveSessionConfig,
} from './useVoiceLiveSession';

const SCRIPTED_DEBUG_PREFIX = '[scripted-lesson]';

function scriptedDebug(message: string, ...data: unknown[]) {
    console.debug(SCRIPTED_DEBUG_PREFIX, message, ...data);
}

export type ScriptedLessonPhase =
    | 'idle'
    | 'connecting'
    | 'reading'
    | 'listening'
    | 'processing'
    | 'completed'
    | 'error';

export interface ScriptedLessonTranscriptEvent {
    id: string;
    speaker: 'ai' | 'participant' | 'system';
    text: string;
    createdAt: string;
}

interface SlideInstructionEntry {
    slide_id: number;
    slide_number: number;
    instructions: string;
}

interface ScriptedTokenResponse {
    key: string;
    endpoint: string;
    model: string;
    baseline_prompt: string;
    slide_instructions: SlideInstructionEntry[];
    start_slide_id: number | null;
    tools: VoiceLiveSessionConfig['tools'];
    voice_session_id: number;
    role: string;
    mode: string;
}

interface ProgressResponse {
    success: boolean;
    session_id?: number;
    slide_id?: number;
    status?: string;
    started_at?: string | null;
    completed_at?: string | null;
    time_spent_seconds?: number | null;
    voice_session_completed?: boolean;
}

type SlideProgressStatus = 'started' | 'completed' | 'session_completed';

/**
 * Fallback for a missed next_slide tool call. Deliberately stricter than the
 * conversational hook's version: it only fires on a short, command-shaped
 * utterance, so a real answer like "I want to move on from last game" does not
 * skip the slide.
 */
const ADVANCE_COMMAND_REGEX = /^(ok(ay)?[,\s]*)?(let'?s\s+|i'?m\s+)?(next( slide| page| question)?|move on|continue|ready|done)[.!]?$/i;
const MAX_FALLBACK_COMMAND_WORDS = 5;

export function isAdvanceCommand(text: string): boolean {
    const trimmed = (text || '').trim();
    if (!trimmed) return false;
    if (trimmed.split(/\s+/).length > MAX_FALLBACK_COMMAND_WORDS) return false;
    return ADVANCE_COMMAND_REGEX.test(trimmed);
}

function errorMessage(err: unknown, fallback: string): string {
    return err instanceof Error ? err.message : fallback;
}

interface UseScriptedLessonSessionOptions {
    lessonPlanId: number | null;
    session: LessonPlanSessionModel;
    onSlideProgress?: (slideId: number, updates: Partial<SessionSlideModel>) => void;
    onSessionProgress?: (sessionId: number, updates: Partial<LessonPlanSessionModel>) => void;
    onNavigationIntent?: (intent: VoiceNavigationIntent) => void;
}

export function useScriptedLessonSession({
    lessonPlanId,
    session,
    onSlideProgress,
    onSessionProgress,
    onNavigationIntent,
}: UseScriptedLessonSessionOptions) {
    const [voiceActive, setVoiceActive] = useState(false);
    const [phase, setPhase] = useState<ScriptedLessonPhase>('idle');
    const [voiceError, setVoiceError] = useState<string | null>(null);
    const [voiceSessionId, setVoiceSessionId] = useState<number | null>(null);
    const [transcriptEvents, setTranscriptEvents] = useState<ScriptedLessonTranscriptEvent[]>([]);
    const [currentAiTranscript, setCurrentAiTranscript] = useState('');
    const [currentSlideId, setCurrentSlideId] = useState<number | null>(null);

    const isActiveRef = useRef(false);
    const voiceSessionIdRef = useRef<number | null>(null);
    const currentAiTranscriptRef = useRef('');
    const currentSlideIdRef = useRef<number | null>(null);
    const baselinePromptRef = useRef('');
    const slideInstructionsRef = useRef<SlideInstructionEntry[]>([]);
    const slideOrderRef = useRef<number[]>([]);
    const advancingRef = useRef(false);

    useEffect(() => { isActiveRef.current = voiceActive; }, [voiceActive]);
    useEffect(() => { voiceSessionIdRef.current = voiceSessionId; }, [voiceSessionId]);
    useEffect(() => { currentAiTranscriptRef.current = currentAiTranscript; }, [currentAiTranscript]);
    useEffect(() => { currentSlideIdRef.current = currentSlideId; }, [currentSlideId]);

    const {
        connect,
        disconnect: disconnectSession,
        sendFunctionResult,
        sendSessionUpdate,
        cancelResponse,
        requestResponse,
        setCallbacks,
    } = useVoiceLiveSession();

    const slideOrder = useMemo<number[]>(
        () => (session?.slides || [])
            .filter((slide): slide is SessionSlideModel => Boolean(slide?.id))
            .sort((a, b) => (a.slide_number ?? 0) - (b.slide_number ?? 0))
            .map(slide => slide.id),
        [session?.slides],
    );

    const totalSlides = slideOrder.length;
    const activeSlideIndex = useMemo(
        () => (currentSlideId ? slideOrder.indexOf(currentSlideId) : -1),
        [currentSlideId, slideOrder],
    );

    const addTranscriptEvent = useCallback((
        speaker: ScriptedLessonTranscriptEvent['speaker'],
        text: string,
    ) => {
        const trimmed = text.trim();
        if (!trimmed) return;
        setTranscriptEvents(prev => [
            ...prev,
            {
                id: `${Date.now()}-${prev.length}`,
                speaker,
                text: trimmed,
                createdAt: new Date().toISOString(),
            },
        ]);
    }, []);

    const logInteraction = useCallback(async (
        eventType: string,
        payload: Record<string, unknown> = {},
    ) => {
        const id = voiceSessionIdRef.current;
        if (!lessonPlanId || !id) return;
        try {
            await ApiClient.post(`/api/lesson-plan/${lessonPlanId}/voice-interaction/`, {
                voice_session_id: id,
                event_type: eventType,
                slide_id: currentSlideIdRef.current,
                ...payload,
            }, { 'Content-Type': 'application/json' });
        } catch (err) {
            scriptedDebug('interaction log failed', err);
        }
    }, [lessonPlanId]);

    /**
     * Record that the participant used a spoken command. Fire-and-forget by
     * design: the caller has already returned the tool result so the AI can
     * speak immediately, and a dropped log is not worth a delay.
     */
    const logCommand = useCallback((toolName: string, callId: string) => {
        void logInteraction('tool_call', {
            tool_name: toolName,
            tool_call_id: callId,
            success: true,
        });
    }, [logInteraction]);

    const recordSlideProgress = useCallback(async (
        slideId: number | null,
        progressStatus: SlideProgressStatus,
        transcript?: string,
    ): Promise<ProgressResponse | null> => {
        const id = voiceSessionIdRef.current;
        if (!lessonPlanId || !id) return null;
        try {
            const res = await ApiClient.post<ProgressResponse>(
                `/api/lesson-plan/${lessonPlanId}/voice-progress/`,
                {
                    voice_session_id: id,
                    slide_id: slideId,
                    status: progressStatus,
                    transcript: transcript ?? '',
                },
                { 'Content-Type': 'application/json' },
            );
            if (!res.success || !res.data) {
                throw new Error(res.error || 'Failed to record lesson progress');
            }
            if (progressStatus !== 'session_completed' && res.data.slide_id) {
                const updates: Partial<SessionSlideModel> = {};
                if (res.data.started_at) updates.started_at = res.data.started_at;
                if (res.data.completed_at) updates.completed_at = res.data.completed_at;
                if (res.data.time_spent_seconds != null) {
                    updates.time_spent_seconds = res.data.time_spent_seconds;
                }
                onSlideProgress?.(res.data.slide_id, updates);
            }
            if (progressStatus === 'session_completed' && res.data.session_id) {
                onSessionProgress?.(res.data.session_id, {
                    completed_at: res.data.completed_at || new Date().toISOString(),
                });
            }
            return res.data;
        } catch (err) {
            const message = errorMessage(err, 'Failed to record lesson progress');
            scriptedDebug('progress record failed', err);
            setVoiceError(message);
            return null;
        }
    }, [lessonPlanId, onSlideProgress, onSessionProgress]);

    const recordSlideResponse = useCallback(async (transcript: string, callId?: string) => {
        const id = voiceSessionIdRef.current;
        if (!lessonPlanId || !id) return;
        try {
            await ApiClient.post(
                `/api/lesson-plan/${lessonPlanId}/voice-slide-response/`,
                {
                    voice_session_id: id,
                    slide_id: currentSlideIdRef.current,
                    transcript,
                    tool_call_id: callId || '',
                },
                { 'Content-Type': 'application/json' },
            );
        } catch (err) {
            scriptedDebug('slide response record failed', err);
        }
    }, [lessonPlanId]);

    const sendInstructionsForSlide = useCallback((slideId: number) => {
        const entry = slideInstructionsRef.current.find(item => item.slide_id === slideId);
        if (!entry) {
            scriptedDebug('no instructions found for slide', { slideId });
            return;
        }
        sendSessionUpdate(`${baselinePromptRef.current}\n\n${entry.instructions}`);
    }, [sendSessionUpdate]);

    const endSession = useCallback(async (
        source: 'voice' | 'button' | 'auto',
        finalSlideId?: number | null,
    ) => {
        if (advancingRef.current) return;
        advancingRef.current = true;
        try {
            const slideId = finalSlideId ?? currentSlideIdRef.current ?? null;
            const result = await recordSlideProgress(slideId, 'session_completed', source);
            await logInteraction('session_ended', {
                success: result?.success !== false,
                error: result ? '' : 'progress record failed',
            });
            setPhase('completed');
            setVoiceActive(false);
            isActiveRef.current = false;
        } finally {
            advancingRef.current = false;
        }
    }, [recordSlideProgress, logInteraction]);

    const advanceToNextSlide = useCallback(async () => {
        if (advancingRef.current) return;
        const order = slideOrderRef.current;
        const currentSlide = currentSlideIdRef.current;
        if (!currentSlide || order.length === 0) return;

        const idx = order.indexOf(currentSlide);
        if (idx < 0) return;

        advancingRef.current = true;
        try {
            setPhase('processing');
            const completedResult = await recordSlideProgress(currentSlide, 'completed');
            if (!completedResult) {
                setPhase('error');
                return;
            }

            const nextSlideId = order[idx + 1];
            if (nextSlideId == null) {
                // Final slide: leave completion to the AI's complete_session call
                // so it can deliver the closing line before we disconnect.
                setPhase('listening');
                return;
            }

            cancelResponse();
            const startedResult = await recordSlideProgress(nextSlideId, 'started');
            if (!startedResult) {
                setPhase('error');
                return;
            }
            setCurrentSlideId(nextSlideId);
            currentSlideIdRef.current = nextSlideId;
            sendInstructionsForSlide(nextSlideId);
            requestResponse();
            setPhase('reading');

            if (session?.id) {
                onNavigationIntent?.({
                    direction: 'next',
                    source: 'voice',
                    sessionId: session.id,
                    slideId: currentSlide,
                });
            }
        } finally {
            advancingRef.current = false;
        }
    }, [
        recordSlideProgress,
        cancelResponse,
        sendInstructionsForSlide,
        requestResponse,
        onNavigationIntent,
        session?.id,
    ]);

    const completeSession = useCallback(async () => {
        await endSession('voice');
        // Let the AI finish speaking the closing line before tearing down the socket.
        window.setTimeout(() => disconnectSession(), 6000);
    }, [endSession, disconnectSession]);

    const handleFunctionCall = useCallback(async (
        name: string,
        callId: string,
        args: VoiceLiveFunctionArgs,
    ) => {
        scriptedDebug('functionCall', { name, args });

        switch (name) {
            case 'record_slide_response': {
                const transcript = typeof args.transcript === 'string' ? args.transcript : '';
                sendFunctionResult(callId, { success: true });
                void recordSlideResponse(transcript, callId);
                setPhase('listening');
                break;
            }
            case 'next_slide': {
                sendFunctionResult(callId, { success: true });
                await advanceToNextSlide();
                break;
            }
            case 'complete_session': {
                sendFunctionResult(callId, { success: true });
                await completeSession();
                break;
            }
            // Logging-only tools: return immediately and log in the background so
            // the reframe or repeat is not delayed by a round trip.
            case 'log_reframe':
            case 'log_repeat': {
                sendFunctionResult(callId, { success: true });
                logCommand(name, callId);
                break;
            }
            default:
                scriptedDebug('unhandled function call', name);
                sendFunctionResult(callId, { success: false, error: 'Unknown tool' });
                break;
        }
    }, [sendFunctionResult, recordSlideResponse, advanceToNextSlide, completeSession, logCommand]);

    const handleParticipantTranscript = useCallback((text: string) => {
        if (!isActiveRef.current) return;
        if (!isAdvanceCommand(text)) return;
        scriptedDebug('advance command fallback fired', text);
        void advanceToNextSlide();
    }, [advanceToNextSlide]);

    const startVoiceLesson = useCallback(async () => {
        if (!lessonPlanId || !session?.id) return;

        setPhase('connecting');
        setVoiceError(null);
        setTranscriptEvents([]);
        setCurrentAiTranscript('');
        setCurrentSlideId(null);
        currentSlideIdRef.current = null;

        try {
            const tokenRes = await ApiClient.get<ScriptedTokenResponse>(
                `/api/lesson-plan/${lessonPlanId}/voice-token/?session_id=${session.id}&mode=scripted`,
            );
            if (!tokenRes.success || !tokenRes.data) {
                throw new Error(tokenRes.error || 'Failed to start scripted facilitator');
            }

            const {
                key,
                endpoint,
                model,
                baseline_prompt,
                slide_instructions,
                start_slide_id,
                tools,
                voice_session_id,
            } = tokenRes.data;

            if (!slide_instructions.length || start_slide_id == null) {
                throw new Error('Lesson plan session has no slides to read');
            }

            baselinePromptRef.current = baseline_prompt;
            slideInstructionsRef.current = slide_instructions;
            slideOrderRef.current = slide_instructions.map(entry => entry.slide_id);
            setVoiceSessionId(voice_session_id);
            voiceSessionIdRef.current = voice_session_id;

            setCallbacks({
                onSessionCreated: () => setPhase('reading'),
                onTranscriptDone: (text: string) => {
                    addTranscriptEvent('participant', text);
                    handleParticipantTranscript(text);
                },
                onTranscriptDelta: (text: string) => {
                    currentAiTranscriptRef.current += text;
                    setCurrentAiTranscript(currentAiTranscriptRef.current);
                },
                onAudioTranscriptDone: (text: string) => {
                    addTranscriptEvent('ai', text);
                    void logInteraction('ai_transcript', { transcript: text });
                    currentAiTranscriptRef.current = '';
                    setCurrentAiTranscript('');
                },
                onFunctionCall: handleFunctionCall,
                onAudioOutputStarted: () => {
                    currentAiTranscriptRef.current = '';
                    setCurrentAiTranscript('');
                    setPhase('reading');
                },
                onAudioOutputStopped: () => {
                    if (isActiveRef.current) setPhase('listening');
                },
                onError: (error: string) => {
                    setVoiceError(error);
                    setPhase('error');
                    void logInteraction('error', { error, success: false });
                },
                onDisconnected: () => {
                    if (isActiveRef.current) {
                        setVoiceError('Scripted facilitator disconnected unexpectedly');
                        setPhase('error');
                    }
                },
            });

            const startEntry = slide_instructions.find(entry => entry.slide_id === start_slide_id)
                || slide_instructions[0];

            const config: VoiceLiveSessionConfig = {
                key,
                endpoint,
                model,
                systemPrompt: `${baseline_prompt}\n\n${startEntry.instructions}`,
                tools,
            };

            await connect(config);
            setCurrentSlideId(startEntry.slide_id);
            currentSlideIdRef.current = startEntry.slide_id;
            setVoiceActive(true);
            isActiveRef.current = true;
            void recordSlideProgress(startEntry.slide_id, 'started');
        } catch (err: unknown) {
            const message = errorMessage(err, 'Failed to start scripted facilitator');
            scriptedDebug('start failed', err);
            setVoiceError(message);
            setPhase('error');
        }
    }, [
        lessonPlanId,
        session?.id,
        connect,
        setCallbacks,
        addTranscriptEvent,
        logInteraction,
        handleFunctionCall,
        handleParticipantTranscript,
        recordSlideProgress,
    ]);

    const stopVoiceLesson = useCallback(async () => {
        if (phase !== 'completed') {
            cancelResponse();
            await endSession('button');
        }
        disconnectSession();
        setVoiceActive(false);
        setPhase('idle');
        setCurrentAiTranscript('');
    }, [phase, cancelResponse, endSession, disconnectSession]);

    /** Button equivalents of the spoken commands; logged without a tool call. */
    const requestReframe = useCallback(() => {
        void logInteraction('tool_call', { tool_name: 'log_reframe', success: true });
        sendSessionUpdate(
            `${baselinePromptRef.current}\n\n${slideInstructionsRef.current.find(
                entry => entry.slide_id === currentSlideIdRef.current,
            )?.instructions || ''}\n\nThe participant just pressed the reframe button. Speak the reframe for this slide word for word now.`,
        );
        requestResponse();
    }, [logInteraction, sendSessionUpdate, requestResponse]);

    const requestRepeat = useCallback(() => {
        void logInteraction('tool_call', { tool_name: 'log_repeat', success: true });
        sendSessionUpdate(
            `${baselinePromptRef.current}\n\n${slideInstructionsRef.current.find(
                entry => entry.slide_id === currentSlideIdRef.current,
            )?.instructions || ''}\n\nThe participant just pressed the repeat button. Repeat your last statement word for word now.`,
        );
        requestResponse();
    }, [logInteraction, sendSessionUpdate, requestResponse]);

    useEffect(() => () => { disconnectSession(); }, [disconnectSession]);

    return {
        voiceActive,
        phase,
        voiceError,
        voiceSessionId,
        transcriptEvents,
        currentAiTranscript,
        currentSlideId,
        activeSlideIndex,
        totalSlides,
        startVoiceLesson,
        stopVoiceLesson,
        advanceToNextSlide,
        requestReframe,
        requestRepeat,
        endSession,
    };
}
